
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, ArrowRight, Trash2, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trip } from '@/types/itinerary';
import { cn } from '@/lib/utils';
import TripDeleteDialog from './TripDeleteDialog';
import { formatDate } from '@/utils/dateUtils';
import { toast } from 'sonner';

interface MyTripCardProps {
  trip: Trip & { isJoined?: boolean };
  onDelete?: () => void;
}

const MyTripCard: React.FC<MyTripCardProps> = ({ trip, onDelete }) => {
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  const handleViewTrip = () => {
    navigate(`/trips/${trip.id}`);
  };

  const handleTripDeleted = () => {
    console.log(`Trip ${trip.id} deleted - triggering parent callback`);
    if (onDelete) {
      onDelete();
    }
  };

  const copyTripCode = () => {
    navigator.clipboard.writeText(trip.code);
    toast.success('Trip code copied to clipboard!');
  };

  return (
    <>
      <Card 
        className={cn(
          "overflow-hidden transition-all duration-300 hover:shadow-lg",
          "border-t-4 border-t-purple-DEFAULT bg-gradient-light"
        )}
        aria-label={`Trip to ${trip.destination || 'Unknown destination'}`}
      >
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="text-xl font-bold font-poppins line-clamp-1 mb-1">{trip.name}</h3>
              <div className="flex items-center text-muted-foreground">
                <MapPin className="w-4 h-4 mr-1 text-purple-DEFAULT" />
                <span className="line-clamp-1">{trip.destination || 'No destination set'}</span>
              </div>
            </div>
            {trip.isJoined && (
              <Badge variant="outline" className="ml-2 bg-white">Guest</Badge>
            )}
          </div>
          
          <div className="space-y-1 mb-4">
            <p className="text-sm">
              <span className="font-medium">Start:</span> {formatDate(trip.start_date)}
            </p>
            <p className="text-sm">
              <span className="font-medium">End:</span> {formatDate(trip.end_date)}
            </p>
            {trip.description && (
              <p className="text-sm mt-2 text-muted-foreground line-clamp-2">{trip.description}</p>
            )}
          </div>

          <div className="flex items-center mt-2 p-2 bg-secondary/30 rounded-md">
            <div className="flex-1">
              <p className="text-sm font-medium">Trip Code:</p>
              <p className="text-md font-mono font-semibold">{trip.code}</p>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={copyTripCode} 
              className="text-purple-DEFAULT hover:text-purple-tertiary"
              aria-label="Copy trip code"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
        
        <CardFooter className="p-4 pt-0 flex flex-col sm:flex-row gap-2">
          <Button 
            onClick={handleViewTrip} 
            className="w-full bg-bright-purple hover:bg-bright-purple/90 shadow-md transform hover:translate-y-[-2px] transition-all duration-200"
            aria-label={`View trip to ${trip.destination || 'Unknown destination'}`}
          >
            View Trip <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          
          <Button
            variant="outline"
            size="icon"
            className="shrink-0 text-red-500 hover:text-red-600 hover:bg-red-50"
            onClick={() => setDeleteDialogOpen(true)}
            aria-label={trip.isJoined ? "Leave trip" : "Delete trip"}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </CardFooter>
      </Card>
      
      <TripDeleteDialog 
        trip={trip} 
        isOpen={deleteDialogOpen} 
        setIsOpen={setDeleteDialogOpen} 
        onDelete={handleTripDeleted} 
      />
    </>
  );
};

export default MyTripCard;
